import { el } from '../../utils/dom';
import { showToast } from '../../components/toast';
import { isAllowedImageMime, uploadAdminImage } from '../../api/ev123Uploads';

export type ImageSlotKey = 'main_image_url' | 'image_1_url' | 'image_2_url' | 'image_3_url';

export type ImageSlotUrls = Record<ImageSlotKey, string>;

export type ImageSlotsApi = {
  element: HTMLElement;
  getUrls: () => ImageSlotUrls;
  setUrls: (urls: Partial<ImageSlotUrls>) => void;
  setActive: (key: ImageSlotKey) => void;
  reset: () => void;
};

type SlotDef = { key: ImageSlotKey; label: string };

const SLOTS: SlotDef[] = [
  { key: 'main_image_url', label: '대표 이미지' },
  { key: 'image_1_url', label: '이미지 1' },
  { key: 'image_2_url', label: '이미지 2' },
  { key: 'image_3_url', label: '이미지 3' },
];

type SlotState = {
  def: SlotDef;
  url: string;
  busy: boolean;
  card: HTMLElement;
  preview: HTMLImageElement;
  empty: HTMLElement;
  status: HTMLElement;
  urlInput: HTMLInputElement;
  fileInput: HTMLInputElement;
  pickBtn: HTMLButtonElement;
  pasteBtn: HTMLButtonElement;
  clearBtn: HTMLButtonElement;
};

export function buildImageSlots(): ImageSlotsApi {
  const root = el('div', { className: 'ev123-card ev123-manage-images' });
  root.append(
    el('div', { className: 'ev123-manage-images__head' }, [
      el('h3', { className: 'ev123-manage-images__title', text: '이미지' }),
      el('p', {
        className: 'ev123-manage-images__hint',
        text: '슬롯 클릭 → Ctrl+V 로 캡처 이미지를 붙여넣거나 파일을 선택하세요. 비공개 저장소(S3)에 보관되며 게시된 사례에서만 공개됩니다.',
      }),
    ]),
  );

  const grid = el('div', { className: 'ev123-manage-images__grid' });
  const states: SlotState[] = SLOTS.map((def) => buildSlot(def));
  for (const s of states) grid.appendChild(s.card);
  root.appendChild(grid);

  let active: ImageSlotKey = 'main_image_url';
  setActive(active);

  // Ctrl+V — 슬롯 영역에 포커스가 있을 때만 처리.
  root.addEventListener('paste', (e) => {
    void onPaste(e);
  });

  function findState(k: ImageSlotKey): SlotState {
    return states.find((s) => s.def.key === k) ?? states[0]!;
  }

  function setActive(k: ImageSlotKey): void {
    active = k;
    for (const s of states) {
      s.card.classList.toggle('ev123-manage-image-slot--active', s.def.key === k);
    }
  }

  function buildSlot(def: SlotDef): SlotState {
    const card = el('div', {
      className: 'ev123-manage-image-slot',
      tabindex: '0',
      role: 'group',
      'aria-label': `${def.label} 업로드 슬롯`,
    });

    const preview = el('img', {
      className: 'ev123-manage-image-slot__preview',
      alt: `${def.label} 미리보기`,
      style: 'display:none',
      decoding: 'async',
    }) as HTMLImageElement;

    const empty = el('div', { className: 'ev123-manage-image-slot__empty' });
    empty.append(
      el('p', { className: 'ev123-manage-image-slot__label', text: def.label }),
      el('p', {
        className: 'ev123-manage-image-slot__placeholder',
        text: '클릭 후 Ctrl+V 또는 파일 선택',
      }),
    );

    const status = el('p', {
      className: 'ev123-manage-image-slot__status',
      style: 'display:none',
    });

    const urlInput = el('input', {
      type: 'url',
      className: 'ev123-input ev123-manage-image-slot__url',
      placeholder: 'URL 직접 입력(고급)',
      'aria-label': `${def.label} URL`,
    }) as HTMLInputElement;

    const fileInput = el('input', {
      type: 'file',
      accept: 'image/jpeg,image/png,image/webp',
      style: 'display:none',
    }) as HTMLInputElement;

    const pickBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: '파일 선택',
    }) as HTMLButtonElement;

    const pasteBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: '붙여넣기',
    }) as HTMLButtonElement;

    const clearBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--danger ev123-btn--sm',
      text: '비우기',
    }) as HTMLButtonElement;

    const actions = el('div', { className: 'ev123-manage-image-slot__actions' });
    actions.append(pickBtn, pasteBtn, clearBtn);

    card.append(preview, empty, status, urlInput, fileInput, actions);

    const state: SlotState = {
      def,
      url: '',
      busy: false,
      card,
      preview,
      empty,
      status,
      urlInput,
      fileInput,
      pickBtn,
      pasteBtn,
      clearBtn,
    };

    card.addEventListener('mousedown', () => setActive(def.key));
    card.addEventListener('focus', () => setActive(def.key), true);

    pickBtn.addEventListener('click', () => {
      setActive(def.key);
      fileInput.click();
    });

    pasteBtn.addEventListener('click', async () => {
      setActive(def.key);
      await onClipboardButton(state);
    });

    clearBtn.addEventListener('click', () => {
      setSlot(state, '');
    });

    fileInput.addEventListener('change', async () => {
      const f = fileInput.files?.[0];
      fileInput.value = '';
      if (!f) return;
      await uploadIntoSlot(state, f);
    });

    urlInput.addEventListener('change', () => {
      const v = urlInput.value.trim();
      setSlot(state, v);
    });

    return state;
  }

  function setSlot(s: SlotState, url: string): void {
    s.url = url;
    s.urlInput.value = url;
    if (url) {
      s.preview.src = url;
      s.preview.style.display = '';
      s.empty.style.display = 'none';
    } else {
      s.preview.removeAttribute('src');
      s.preview.style.display = 'none';
      s.empty.style.display = '';
    }
    setBusy(s, false);
  }

  function setBusy(s: SlotState, busy: boolean, msg = ''): void {
    s.busy = busy;
    s.pickBtn.disabled = busy;
    s.pasteBtn.disabled = busy;
    s.clearBtn.disabled = busy;
    if (busy) {
      s.status.textContent = msg || '업로드 중…';
      s.status.style.display = '';
    } else {
      s.status.textContent = '';
      s.status.style.display = 'none';
    }
  }

  async function uploadIntoSlot(s: SlotState, file: File): Promise<void> {
    if (!isAllowedImageMime(file.type)) {
      showToast('지원하지 않는 형식입니다. (jpg/png/webp)');
      return;
    }
    setBusy(s, true);
    const r = await uploadAdminImage(file);
    if (!r.ok) {
      setBusy(s, false);
      showToast(r.message || '업로드에 실패했습니다.');
      return;
    }
    setSlot(s, r.data.url);
    showToast(r.data.reused ? '동일 파일을 재사용했습니다.' : '업로드되었습니다.');
  }

  async function onPaste(e: ClipboardEvent): Promise<void> {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const file = pickImageFromClipboard(items);
    if (!file) return;
    e.preventDefault();
    const s = findState(active);
    await uploadIntoSlot(s, file);
  }

  async function onClipboardButton(s: SlotState): Promise<void> {
    const nav = navigator as Navigator & { clipboard?: { read?: () => Promise<unknown> } };
    if (!nav.clipboard || typeof nav.clipboard.read !== 'function') {
      showToast('이 브라우저는 붙여넣기 버튼을 지원하지 않습니다. Ctrl+V를 사용해 주세요.');
      return;
    }
    try {
      const items = (await nav.clipboard.read()) as unknown as Array<{
        types: string[];
        getType: (t: string) => Promise<Blob>;
      }>;
      for (const item of items) {
        const t = item.types.find((x) => isAllowedImageMime(x));
        if (!t) continue;
        const blob = await item.getType(t);
        const ext = t.split('/').pop() || 'png';
        const file = new File([blob], `pasted.${ext}`, { type: t });
        await uploadIntoSlot(s, file);
        return;
      }
      showToast('클립보드에 이미지가 없습니다.');
    } catch {
      showToast('클립보드 권한이 필요합니다. 슬롯을 클릭한 뒤 Ctrl+V를 사용해 주세요.');
    }
  }

  function pickImageFromClipboard(items: DataTransferItemList): File | null {
    for (let i = 0; i < items.length; i++) {
      const it = items[i]!;
      if (it.kind !== 'file') continue;
      const f = it.getAsFile();
      if (f && isAllowedImageMime(f.type)) return f;
    }
    return null;
  }

  function getUrls(): ImageSlotUrls {
    const out = {} as ImageSlotUrls;
    for (const s of states) out[s.def.key] = s.url;
    return out;
  }

  function setUrls(urls: Partial<ImageSlotUrls>): void {
    for (const s of states) {
      const v = urls[s.def.key];
      setSlot(s, typeof v === 'string' ? v : '');
    }
  }

  function reset(): void {
    for (const s of states) setSlot(s, '');
    setActive('main_image_url');
  }

  return {
    element: root,
    getUrls,
    setUrls,
    setActive,
    reset,
  };
}
